/**
 * CustomerERP — Audit Log (Faz 0). Kaynak: GET /api/audit (yalnız admin).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Input, Btn } from "@/components/UI";
import { RefreshCw, ChevronDown, ChevronRight } from "lucide-react";

function Row({ a, idx }) {
  const [open, setOpen] = useState(false);
  const hasDiff = a.before || a.after;
  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`audit-row-${idx}`}>
        <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">{(a.timestamp || "").replace("T", " ").slice(0, 19)}</td>
        <td className="px-3 py-2 text-xs text-slate-700">{a.user_email || a.user_id || "—"}</td>
        <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium">{a.action}</span></td>
        <td className="px-3 py-2 text-xs text-slate-700">{a.entity}</td>
        <td className="px-3 py-2 text-[11px] font-mono text-slate-500 truncate max-w-[180px]">{a.entity_id}</td>
        <td className="px-3 py-2 text-right">
          {hasDiff && (
            <button onClick={() => setOpen(!open)} className="text-slate-500 hover:text-slate-800" data-testid={`audit-toggle-${idx}`}>
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/70">
          <td colSpan={6} className="px-3 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
              <pre className="bg-white border border-slate-200 rounded p-2 overflow-auto max-h-60"><b>Önce</b>{"\n"}{JSON.stringify(a.before, null, 2)}</pre>
              <pre className="bg-white border border-slate-200 rounded p-2 overflow-auto max-h-60"><b>Sonra</b>{"\n"}{JSON.stringify(a.after, null, 2)}</pre>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true); setErr("");
    api.get("/audit", { limit: 500 })
      .then((r) => setRows(r || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Audit kayıtları yüklenemedi."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    if (!s) return rows;
    return rows.filter((a) => [a.user_email, a.action, a.entity, a.entity_id]
      .some((v) => (v || "").toString().toLocaleLowerCase("tr").includes(s)));
  }, [rows, q]);

  return (
    <div data-testid="audit-log-page">
      <PageHeader title="Audit Log" description="Sistemde yapılan değişikliklerin kaydı"
        actions={<Btn variant="secondary" onClick={load} data-testid="audit-refresh"><RefreshCw size={14} /> Yenile</Btn>} />
      <Card className="p-4">
        <div className="mb-3 max-w-sm">
          <Input placeholder="Kullanıcı, işlem veya kayıt türünde ara…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" />
        </div>
        {err && <div className="text-sm text-rose-600 mb-2" data-testid="audit-error">{err}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Zaman</th><th className="px-3 py-2">Kullanıcı</th>
                <th className="px-3 py-2">İşlem</th><th className="px-3 py-2">Kayıt Türü</th>
                <th className="px-3 py-2">Kayıt</th><th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => <Row key={i} a={a} idx={i} />)}
            </tbody>
          </table>
          {!loading && filtered.length === 0 && !err && (
            <div className="text-sm text-slate-500 py-6 text-center" data-testid="audit-empty">Kayıt bulunamadı.</div>
          )}
          {loading && <div className="text-sm text-slate-500 py-6 text-center">Yükleniyor…</div>}
        </div>
      </Card>
    </div>
  );
}
